import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface DashboardFilters {
  timeRange: number;
  ProviderID: string | null;
}

export interface GenericResponse<T> {
  payload?: T;
  error?: { status: number; message: string };
  dataUpdateDate?: string;
}

export interface NameCountDto {
  Name: string;
  Count: number;
}

export interface XyPointDto {
  x: string;
  y: number;
}

export interface TrendDatasetDto {
  name: string;
  data: XyPointDto[];
}

export interface TncidsTrendsDto {
  datasets: TrendDatasetDto[];
}

export interface GeoVolumeDto {
  CountryCode: string;
  DistrictCode?: string;
  TNCIDs: number;
}

export interface SociodemoRowDto {
  Label: string;
  Male: number;
  Female: number;
  Total: number;
  Unknown: number;
}

export interface CategoryValueDto {
  category: string;
  value: number;
}

/**
 * Calls the legacy publisher-dashboard endpoints (same `/webapp/dashboard/*`
 * API the admin panel's publisher-dashboard page uses) so the client app can
 * show the same figures with its own design.
 */
@Injectable({ providedIn: 'root' })
export class PublisherDashboardApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getActiveUsers(filters: DashboardFilters): Observable<GenericResponse<number>> {
    return this.post('/webapp/dashboard/count/tncids-active', filters);
  }

  getNewUsers(filters: DashboardFilters): Observable<GenericResponse<number>> {
    return this.post('/webapp/dashboard/count/tncids-new', filters);
  }

  getEnrichedUsers(filters: DashboardFilters): Observable<GenericResponse<number>> {
    return this.post('/webapp/dashboard/count/tncids-enriched', filters);
  }

  getGoalsCount(filters: DashboardFilters, type: 'tncids' | 'events'): Observable<GenericResponse<number>> {
    return this.post(`/webapp/dashboard/count/goals?type=${type}`, filters);
  }

  getEmailsCount(filters: DashboardFilters): Observable<GenericResponse<number>> {
    return this.post('/webapp/dashboard/count/emails', filters);
  }

  getMaidsCount(filters: DashboardFilters): Observable<GenericResponse<number>> {
    return this.post('/webapp/dashboard/count/maids', filters);
  }

  getTncidsTrends(filters: DashboardFilters): Observable<GenericResponse<TncidsTrendsDto>> {
    return this.post('/webapp/dashboard/overview/tncids-trends', filters);
  }

  getGoalsChart(filters: DashboardFilters): Observable<GenericResponse<NameCountDto[]>> {
    return this.post('/webapp/dashboard/overview/goals-chart', filters);
  }

  getExternalIds(filters: DashboardFilters): Observable<GenericResponse<NameCountDto[]>> {
    return this.post('/webapp/dashboard/overview/external-ids', filters);
  }

  getGeolocations(filters: DashboardFilters, countryCode?: string): Observable<GenericResponse<GeoVolumeDto[]>> {
    const suffix = countryCode ? `?countryCode=${encodeURIComponent(countryCode)}` : '';
    return this.post(`/webapp/dashboard/count/geolocations${suffix}`, filters);
  }

  getSociodemos(filters: DashboardFilters): Observable<GenericResponse<SociodemoRowDto[]>> {
    return this.post('/webapp/dashboard/count/tncids-sociodemos', filters);
  }

  getTncidsPerProvider(filters: DashboardFilters): Observable<GenericResponse<XyPointDto[]>> {
    return this.post('/webapp/dashboard/overview/tncids-per-provider', filters);
  }

  getTechnologyBrowser(filters: DashboardFilters): Observable<GenericResponse<CategoryValueDto[]>> {
    return this.post('/webapp/dashboard/count/technology-browser', filters);
  }

  getTechnologyDevice(filters: DashboardFilters): Observable<GenericResponse<CategoryValueDto[]>> {
    return this.post('/webapp/dashboard/count/technology-device', filters);
  }

  getTechnologyOs(filters: DashboardFilters): Observable<GenericResponse<CategoryValueDto[]>> {
    return this.post('/webapp/dashboard/count/technology-os', filters);
  }

  getTncidsTags(filters: DashboardFilters): Observable<GenericResponse<XyPointDto[]>> {
    return this.post('/webapp/dashboard/overview/tncids-tags', filters);
  }

  private post<T>(path: string, body: DashboardFilters): Observable<GenericResponse<T>> {
    const ep = this.composeEndpoint(path);
    return this.http.post<GenericResponse<T>>(ep.url, { ...body }, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res ?? {}),
      catchError(err => { console.error(`[publisher-dashboard-api] ${path} failed`, err); return throwError(() => err); })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) path = `/${path}`;
    return { url: `${info.baseUrl}${path}`, useRemote: info.useRemote };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    if (useRemote) {
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const token = this.authService.getTncApiToken();
      if (token) headers = headers.set('Authorization', token);
      return headers;
    }
    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) return '';
    return url.replace(/\/+$/u, '');
  }
}
